import { ReactNode, useId, useState } from 'react'

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

import { Text } from '@/components/atoms'
import { Icon } from '@/components/V2/atoms/Icon'
import { cn } from '@/lib/utils'

import { Tooltip, TooltipTrigger, TooltipContent } from './Tooltip'

export interface RichTooltipProps {
  /** Optional title displayed in bold */
  title?: string
  /** Required description content */
  description: ReactNode | string
  /** The trigger element */
  children: ReactNode
  /** Which side the tooltip appears on. Default: 'top' */
  side?: TooltipPrimitive.Positioner.Props['side']
  /** Which alignment the tooltip appears on. Default: 'center' */
  align?: TooltipPrimitive.Positioner.Props['align']
  /** Color variant. Default: 'dark' */
  variant?: 'dark' | 'light'
  /** Show close button (always shown when title is present) */
  showCloseButton?: boolean
  /** Custom width. Default: '196px' */
  width?: string
  /** Whether to show the tooltip on click instead of hover */
  clickToOpen?: boolean
}

const variantStyles = {
  dark: {
    panel: 'bg-primary-400',
    arrow: 'bg-primary-400',
    title: 'text-surface-white-50',
    description: 'text-surface-white-300',
    closeButton: 'text-surface-white-50 hover:text-surface-white-100'
  },
  light: {
    panel: 'bg-surface-white-50 ring-1 ring-surface-white-200 shadow-lg',
    arrow: 'bg-surface-white-50',
    title: 'text-surface-black-50',
    description: 'text-surface-white-400',
    closeButton: 'text-surface-black-50 hover:text-surface-black-400'
  }
} as const

export function RichTooltip({
  title,
  description,
  children,
  side = 'top',
  align = 'center',
  variant = 'dark',
  showCloseButton,
  width = '196px',
  clickToOpen = false
}: RichTooltipProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const titleId = useId()

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
  }

  const handleTriggerClick = () => {
    if (clickToOpen) {
      setIsOpen(prev => !prev)
    }
  }

  const styles = variantStyles[variant]
  const shouldShowCloseButton = showCloseButton ?? !!title

  return (
    <Tooltip open={isOpen} onOpenChange={handleOpenChange}>
      <TooltipTrigger
        render={children as React.ReactElement}
        onClick={clickToOpen ? handleTriggerClick : undefined}
      />
      <TooltipContent
        side={side}
        align={align}
        className={cn('flex flex-col gap-2 rounded p-4', styles.panel)}
        arrowClassName={styles.arrow}
        style={{ width }}
        aria-labelledby={title ? titleId : undefined}
      >
        {shouldShowCloseButton && (
          <button
            type='button'
            onClick={() => setIsOpen(false)}
            className={cn(
              'absolute right-4 top-4 cursor-pointer leading-none',
              styles.closeButton
            )}
            aria-label='Close tooltip'
          >
            <Icon name='close' size='md' />
          </button>
        )}
        {title && (
          <Text
            level='span'
            size='body-md'
            weight='bold'
            color={styles.title}
            id={titleId}
          >
            {title}
          </Text>
        )}
        <Text
          level={typeof description === 'string' ? 'p' : 'div'}
          size='caption-md'
          weight='regular'
          color={styles.description}
        >
          {description}
        </Text>
      </TooltipContent>
    </Tooltip>
  )
}
